import React from 'react'
import { CSSTransition } from 'react-transition-group'
import styled from 'styled-components'
import { v4 as genId } from 'uuid'
import { Types } from '../../types'
import Category from './Category'
import { MobileMenuContext } from '../../contexts/MobileMenu';
import Item from './Item'
import { SocialLink, SocialLinks } from './Sidebar'
import { ReactSVG } from 'react-svg'

// Props extended interface
interface IMenu extends Types.AppOptions {
	items: Types.MenuItem[]
	social?: Types.MenuItem[]
	// If bootstrapping the data iteration
	boot?: boolean
	// Forwarded menuId reference from an Item parent 
	forwardedRef?: any
	// Forwarded menu title for the category name
	forwardedTitle?: any
}

/**
 * Menu component to render a list of links
 * destructured @param boot Initial iteration boolean
 * destructured @param forwardedRef Parent menu ID
 * destructured @param forwardedTitle Parent menu title
 * destructured @param data Data to be iterated
 * rest @param props IMegaMenuOptions
 * @returns React FC
 */
const Menu: React.FC<IMenu> = ({
	boot,
	forwardedRef,
	forwardedTitle,
	items,
	social,
	...props
}) => {
	// Context state + next handler
	const { state, handleNext } = React.useContext(MobileMenuContext)

	// Reference to the bootstrapping boolean accross the iteration
	const bootRef = React.useRef(boot)

	const menuIdRef = React.useRef(genId())
	const menuId = forwardedRef?.current || menuIdRef.current

	// Initial menu loader
	React.useEffect(() => {
		bootRef.current && handleNext(menuIdRef.current)
		bootRef.current = false
	}, [bootRef, handleNext, menuId])

	const { config } = props

	// Last `actives` element gets `current` class
	const classes =
		state.actives[state.actives.length - 1] === menuId ? 'current' : ''

	return (
		<>
			{/* Animated category */}
			<CSSTransition
				in={(boot ? state.collapsing || state.expanded : state.actives.includes(menuId))}
				timeout={config.general.timingBase}
				classNames={'category'}
				unmountOnExit
			>
				<StyledCategory boot={boot} {...props} className={classes}>
					<Category
						{...props}
						categoryTitle={forwardedTitle || config.mobileMenu.menuTitle}
						topLevel={forwardedTitle ? false : true}
					/>
				</StyledCategory>
			</CSSTransition>
			{/* Animated menu */}
			<CSSTransition
				in={boot ? state.collapsing || state.expanded : state.actives.includes(menuId)}
				timeout={config.general.timingBase}
				classNames={'menu'}
				unmountOnExit
			>
				<StyledMenu {...props} className={classes}>
					{/* Menu items */}
					{items.map((item, idx: React.Key) => {
						return <Item {...props} key={idx} {...item} />
					})}
					{(social && boot) && (
						<SocialLinks>
							{social.map((item: any, index: React.Key) => (
								<SocialLink href={item.permalink} target="_blank" rel="noreferrer" aria-label={item.label} key={`social-item_${index}`}>
									<ReactSVG height={24} width="auto" className="item-icon" src={item.icon} />
								</SocialLink>
							))}
						</SocialLinks>
					)}
				</StyledMenu>
			</CSSTransition>
		</>
	)
}

// Extended StyledMenu interface
interface IStyledMenu extends Types.AppOptions {
	// Boot needed to distinguish between top level menu and others
	boot?: boolean
}

// Category top positioned
const StyledCategory = styled.div<IStyledMenu>`
  ${({ boot: b, config: c, theme: t }) => `
  position: absolute;
  top: 0;
  width: 100%;
  height: 4em;
  z-index: 1;

  // Translate position if booted, otherwise fade-in/out
  &.category-enter {
    ${b
			? `transform: translate(${c.mobileMenu.inverse ? '-100%, 0' : '100%, 0'})`
			: `opacity: 0`
		};
  }

  &.category-enter-active {
    ${b ? `transform: translate(0, 0)` : 'opacity: 1'};
    ${!c.general.turnOffTransitions
			? `transition: ${b ? 'transform' : 'opacity'} ${c.general.timingBase}ms ${c.general.transitionFunction
			};`
			: ''
		}
  }

  &.category-enter-done {
    ${b ? `transform: translate(0, 0)` : 'opacity: 1'};
  }

  &.category-exit-enter {
    ${b
			? `transform: translate(${c.mobileMenu.inverse ? '-100%, 0' : '100%, 0'})`
			: 'opacity: 0'
		};
  }

  &.category-exit-active {
    ${b
			? `transform: translate(${c.mobileMenu.inverse ? '-100%, 0' : '100%, 0'})`
			: 'opacity: 0'
		};
    ${!c.general.turnOffTransitions
			? `transition: ${b ? 'transform' : 'opacity'} ${c.general.timingBase}ms ${c.general.transitionFunction
			};`
			: ''
		}
  }
`}
`

// Top positioned menu with top padding + custom scrollbar
const StyledMenu = styled.ul<Types.AppOptions>`
  ${({ config: c, theme: t }) => `
  list-style-type: none;
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  padding-top: 4em;
  overflow-y: hidden;
  overflow-x: hidden;
  background: ${t.mobileMenu.itemBackground};

  &::-webkit-scrollbar {
    width: 0;
  }

  &::-webkit-scrollbar-button {
    background: ${t.general.scrollBarColor};
    height: ${t.general.scrollBarSize}em;
  }

  &::-webkit-scrollbar-thumb {
    background: ${t.general.scrollBarColor};
  }

  &.current {
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: ${t.mobileMenu.scrollBarSize}px;
    }
  }

  // Translate position
  &.menu-enter {
    transform: translate(${c.mobileMenu.inverse ? '-100%, 0' : '100%, 0'});
  }

  &.menu-enter-active {
   transform: translate(0, 0);
    ${!c.general.turnOffTransitions
			? `transition: transform ${c.general.timingBase}ms ${c.general.transitionFunction};`
			: ''
		}
  }

  &.menu-enter-done {
    transform: translate(0, 0);
  }

  &.menu-exit-enter {
    transform: translate(${c.mobileMenu.inverse ? '-100%, 0' : '100%, 0'});
  }

  &.menu-exit-active {
    transform: translate(${c.mobileMenu.inverse ? '-100%, 0' : '100%, 0'});
    ${!c.general.turnOffTransitions
			? `transition: transform ${c.general.timingBase}ms ${c.general.transitionFunction};`
			: ''
		}
  }
`}
`

export default Menu
